import React, { useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Icon from '../AppIcon'
import { useAppConfig } from '../../lib/AppConfigContext'

const focusables = node => [...node.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')]
  .filter(element => element.getBoundingClientRect().width && element.getBoundingClientRect().height)

export default function MobileMenuDrawer({ isOpen, onClose, items, branding }) {
  const { config } = useAppConfig()
  const location = useLocation()
  const panel = useRef(null)
  const previousPath = useRef(location.pathname)

  useEffect(() => {
    if (previousPath.current !== location.pathname) onClose()
    previousPath.current = location.pathname
  }, [location.pathname, onClose])

  useEffect(() => {
    if (!isOpen) return
    const previousFocus = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    focusables(panel.current)[0]?.focus()
    const handleKey = event => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); return }
      if (event.key !== 'Tab') return
      const controls = focusables(panel.current)
      const first = controls[0], last = controls.at(-1)
      if (!first) { event.preventDefault(); return }
      if (event.shiftKey && (document.activeElement === first || !panel.current.contains(document.activeElement))) {
        event.preventDefault(); last.focus()
      } else if (!event.shiftKey && (document.activeElement === last || !panel.current.contains(document.activeElement))) {
        event.preventDefault(); first.focus()
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKey)
      if (previousFocus?.isConnected) previousFocus.focus()
    }
  }, [isOpen, onClose])

  if (!isOpen) return null
  const brand = branding || config?.branding || {}
  return <div className="fixed inset-0 z-[80] bg-black/35" onClick={event => { if (event.target === event.currentTarget) onClose() }}>
    <div ref={panel} role="dialog" aria-modal="true" aria-label="Mobile navigation menu" className="flex h-[100dvh] w-[min(320px,calc(100vw-32px))] flex-col border-r border-border bg-card shadow-elevation-4">
      <div className="flex min-h-[88px] items-center justify-between border-b border-border px-5">
        <div><h2 className="text-xl font-semibold tracking-tight text-foreground">{brand.shortName || 'GoldBuy'}</h2><p className="mt-1 text-[10px] font-medium uppercase tracking-[0.12em] text-foreground">Business workspace</p></div>
        <button type="button" onClick={onClose} aria-label="Close mobile menu" className="flex h-11 w-11 items-center justify-center rounded-lg text-foreground hover:bg-muted"><Icon name="X" size={20} /></button>
      </div>
      <nav aria-label="Workspace navigation" className="flex-1 space-y-1 overflow-y-auto px-4 py-6">
        {items?.map(item => {
          const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/')
          return <div key={item.path}>
            <Link to={item.path} onClick={onClose} aria-current={active ? 'page' : undefined} className={'flex min-h-12 items-center gap-3 rounded-lg px-3 text-sm font-medium ' + (active ? 'bg-foreground text-white' : 'text-foreground hover:bg-muted hover:text-foreground')}><Icon name={item.icon} size={19} />{item.label}</Link>
            {item.children?.map(child => <Link key={child.path} to={child.path} onClick={onClose} className="ml-6 flex min-h-11 items-center rounded-lg px-3 text-sm text-foreground hover:bg-muted">{child.label}</Link>)}
          </div>
        })}
      </nav>
      <p className="border-t border-border px-6 py-5 text-xs text-foreground">{brand.appName || 'GoldBuy Portal'}</p>
    </div>
  </div>
}
